import { useAnimals, useRegisterEvent, type AnimalSummary } from '@/api/hooks';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { WeightField } from '@/components/ui/WeightDialPicker';
import { getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { eventTypeLabel, todayIso } from '@/lib/format';
import { MILKING_SHIFT_OPTIONS } from '@/lib/dairy';
import { useLocalePrefs } from '@/contexts/LocaleContext';
import type { LucideIcon } from 'lucide-react';
import {
  Baby,
  Combine,
  Droplets,
  MilkOff,
  Scale,
  Stethoscope,
  Syringe,
  TestTubeDiagonal,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export const EVENT_TYPES: {
  value: string;
  endpoint: string;
  fields: string[];
  icon: LucideIcon;
}[] = [
  {
    value: 'weighing',
    endpoint: 'weighing',
    fields: ['animal_id', 'weight_kg'],
    icon: Scale,
  },
  {
    value: 'weaning',
    endpoint: 'weaning',
    fields: ['animal_id', 'weight_kg'],
    icon: MilkOff,
  },
  {
    value: 'natural_mount',
    endpoint: 'natural-mount',
    fields: ['cow_id', 'bull_id'],
    icon: Combine,
  },
  {
    value: 'insemination',
    endpoint: 'insemination',
    fields: ['cow_id', 'bull_id'],
    icon: TestTubeDiagonal,
  },
  {
    value: 'pregnancy_diagnosis',
    endpoint: 'pregnancy-diagnosis',
    fields: ['cow_id', 'bull_id', 'cover_event_id', 'is_pregnant'],
    icon: Stethoscope,
  },
  {
    value: 'birth',
    endpoint: 'birth',
    fields: ['cow_id', 'calf_id', 'calf_sex'],
    icon: Baby,
  },
  {
    value: 'milk_weighing',
    endpoint: 'milk-weighing',
    fields: ['cow_id', 'production_liters', 'milking_shift'],
    icon: Droplets,
  },
  {
    value: 'medication',
    endpoint: 'medication',
    fields: [
      'animal_id',
      'treatment_kind',
      'product_name',
      'dose',
      'route',
      'product_batch',
      'next_dose_date',
    ],
    icon: Syringe,
  },
];

export const REPRODUCTION_EVENT_TYPES = [
  'natural_mount',
  'insemination',
  'pregnancy_diagnosis',
] as const;

type EventFormState = {
  event_date: string;
  notes: string;
  responsible_name: string;
  animal_id: string;
  cow_id: string;
  bull_id: string;
  calf_id: string;
  calf_sex: string;
  calf_internal_number: string;
  calf_name: string;
  weight_kg: string;
  production_liters: string;
  milking_shift: string;
  cover_event_id: string;
  is_pregnant: string;
  treatment_kind: string;
  product_name: string;
  dose: string;
  route: string;
  product_batch: string;
  next_dose_date: string;
};

function buildFormState(animal?: AnimalSummary | null): EventFormState {
  const id = animal ? String(animal.id) : '';

  return {
    event_date: todayIso(),
    notes: '',
    responsible_name: '',
    animal_id: id,
    cow_id: animal?.sex === 'female' ? id : '',
    bull_id: animal?.sex === 'male' ? id : '',
    calf_id: '',
    calf_sex: 'female',
    calf_internal_number: '',
    calf_name: '',
    weight_kg: '',
    production_liters: '',
    milking_shift: 'morning',
    cover_event_id: '',
    is_pregnant: 'true',
    treatment_kind: 'vaccination',
    product_name: '',
    dose: '',
    route: '',
    product_batch: '',
    next_dose_date: '',
  };
}

function applyPresetToType(form: EventFormState, animal: AnimalSummary | null | undefined, type: string) {
  const id = animal ? String(animal.id) : '';
  const config = EVENT_TYPES.find((t) => t.value === type);
  if (!config || !animal) return form;

  const next = { ...form };

  if (config.fields.includes('animal_id')) next.animal_id = id;
  if (config.fields.includes('cow_id') && animal.sex === 'female') next.cow_id = id;
  if (config.fields.includes('bull_id') && animal.sex === 'male') next.bull_id = id;

  return next;
}

export function EventFormModal({
  open,
  onClose,
  propertyId,
  presetAnimal = null,
  allowedTypes,
  title = 'Registrar evento',
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number;
  presetAnimal?: AnimalSummary | null;
  allowedTypes?: readonly string[];
  title?: string;
}) {
  useLocalePrefs(); // re-render ao mudar idioma (labels via eventTypeLabel)
  const { data: animals } = useAnimals({ property_id: propertyId });
  const typeOptions = useMemo(
    () =>
      allowedTypes
        ? EVENT_TYPES.filter((item) => allowedTypes.includes(item.value))
        : [...EVENT_TYPES],
    [allowedTypes],
  );
  const [type, setType] = useState(typeOptions[0]?.value ?? EVENT_TYPES[0].value);
  const [error, setError] = useState('');
  const [form, setForm] = useState(() => buildFormState(presetAnimal));
  const [calfMode, setCalfMode] = useState<'existing' | 'new'>('existing');

  const config = typeOptions.find((t) => t.value === type) ?? typeOptions[0] ?? EVENT_TYPES[0];
  const register = useRegisterEvent(config.endpoint);

  useEffect(() => {
    if (!open) return;
    const defaultType = typeOptions[0]?.value ?? EVENT_TYPES[0].value;
    setType(defaultType);
    setError('');
    setCalfMode('existing');
    setForm(applyPresetToType(buildFormState(presetAnimal), presetAnimal, defaultType));
  }, [open, presetAnimal, typeOptions]);

  const femaleOptions = (animals?.animals.filter((a) => a.sex === 'female') ?? []).map((a) => ({
    value: a.id,
    label: `${a.internal_number}${a.animal_name ? ` — ${a.animal_name}` : ''}`,
  }));
  const maleOptions = (animals?.animals.filter((a) => a.sex === 'male') ?? []).map((a) => ({
    value: a.id,
    label: `${a.internal_number}${a.animal_name ? ` — ${a.animal_name}` : ''}`,
  }));
  const allOptions = (animals?.animals ?? []).map((a) => ({
    value: a.id,
    label: `${a.internal_number}${a.animal_name ? ` — ${a.animal_name}` : ''}`,
  }));

  function handleTypeChange(nextType: string) {
    setType(nextType);
    setForm((current) => applyPresetToType(current, presetAnimal, nextType));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (config.fields.includes('weight_kg') && type !== 'weaning' && !form.weight_kg.trim()) {
      setError('Informe o peso.');
      return;
    }

    if (config.fields.includes('production_liters') && !form.production_liters.trim()) {
      setError('Informe a produção em litros.');
      return;
    }

    if (config.fields.includes('product_name') && !form.product_name.trim()) {
      setError('Informe o produto.');
      return;
    }

    if (config.fields.includes('calf_id')) {
      if (calfMode === 'existing' && !form.calf_id) {
        setError('Selecione o bezerro(a) ou cadastre uma cria nova.');
        return;
      }
      if (calfMode === 'new' && !form.calf_internal_number.trim()) {
        setError('Informe o código da cria.');
        return;
      }
    }

    const body: Record<string, unknown> = {
      property_id: propertyId,
      event_date: form.event_date,
      notes: form.notes || null,
      responsible_name: form.responsible_name || null,
    };

    if (config.fields.includes('animal_id')) body.animal_id = Number(form.animal_id);
    if (config.fields.includes('cow_id')) body.cow_id = Number(form.cow_id);
    if (config.fields.includes('bull_id')) body.bull_id = form.bull_id ? Number(form.bull_id) : null;
    if (config.fields.includes('calf_id')) {
      if (calfMode === 'existing') {
        body.calf_id = Number(form.calf_id);
      } else {
        body.create_calf = true;
        body.calf_internal_number = form.calf_internal_number.trim();
        if (form.calf_name.trim()) body.calf_name = form.calf_name.trim();
      }
    }
    if (config.fields.includes('calf_sex')) body.calf_sex = form.calf_sex;
    if (config.fields.includes('weight_kg')) {
      body.weight_kg = form.weight_kg.trim() ? Number(form.weight_kg) : null;
    }
    if (config.fields.includes('production_liters')) {
      body.production_liters = Number(form.production_liters);
    }
    if (config.fields.includes('milking_shift')) {
      body.milking_shift = form.milking_shift;
    }
    if (config.fields.includes('cover_event_id')) {
      body.cover_event_id = form.cover_event_id ? Number(form.cover_event_id) : null;
    }
    if (config.fields.includes('is_pregnant')) body.is_pregnant = form.is_pregnant === 'true';
    if (config.fields.includes('treatment_kind')) body.treatment_kind = form.treatment_kind;
    if (config.fields.includes('product_name')) body.product_name = form.product_name.trim();
    if (config.fields.includes('dose') && form.dose.trim()) body.dose = form.dose.trim();
    if (config.fields.includes('route') && form.route) body.route = form.route;
    if (config.fields.includes('product_batch') && form.product_batch.trim()) {
      body.product_batch = form.product_batch.trim();
    }
    if (config.fields.includes('next_dose_date') && form.next_dose_date) {
      body.next_dose_date = form.next_dose_date;
    }

    try {
      await register.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao registrar evento.'));
    }
  }

  const typeGridCols =
    typeOptions.length <= 3
      ? 'grid-cols-3'
      : typeOptions.length <= 4
        ? 'grid-cols-2 sm:grid-cols-4'
        : 'grid-cols-2 sm:grid-cols-4';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="event-form-modal" loading={register.isPending}>
            Registrar
          </Button>
        </>
      }
    >
      <form id="event-form-modal" onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-manejai-forest">Tipo de evento</legend>
          <div className={cn('grid gap-2', typeGridCols)}>
            {typeOptions.map((eventType) => {
              const Icon = eventType.icon;
              const selected = type === eventType.value;
              const label = eventTypeLabel(eventType.value);
              return (
                <button
                  key={eventType.value}
                  type="button"
                  title={label}
                  aria-pressed={selected}
                  onClick={() => handleTypeChange(eventType.value)}
                  className={cn(
                    'flex flex-col items-center gap-2 rounded-2xl border px-2 py-3 text-center transition-colors',
                    selected
                      ? 'border-manejai-forest bg-manejai-forest/5 text-manejai-forest shadow-sm'
                      : 'border-manejai-sand bg-white text-manejai-bark/80 hover:border-manejai-sage hover:bg-manejai-cream/40',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-xl',
                      selected ? 'bg-manejai-forest text-white' : 'bg-manejai-sand/60 text-manejai-forest',
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <span className="min-h-[2rem] text-[11px] font-medium leading-tight">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <DateInput
          label="Data"
          required
          value={form.event_date}
          onChange={(event_date) => setForm({ ...form, event_date })}
        />

        <Input
          label="Responsável"
          value={form.responsible_name}
          onChange={(e) => setForm({ ...form, responsible_name: e.target.value })}
          placeholder="Nome de quem realizou o manejo"
        />

        {config.fields.includes('animal_id') && (
          <Select
            label="Animal"
            required
            value={form.animal_id}
            onChange={(e) => setForm({ ...form, animal_id: e.target.value })}
            placeholder="Selecione…"
            options={allOptions}
          />
        )}
        {config.fields.includes('cow_id') && (
          <Select
            label="Vaca"
            required
            value={form.cow_id}
            onChange={(e) => setForm({ ...form, cow_id: e.target.value })}
            placeholder="Selecione…"
            options={femaleOptions}
          />
        )}
        {config.fields.includes('bull_id') && (
          <Select
            label="Touro / sêmen"
            value={form.bull_id}
            onChange={(e) => setForm({ ...form, bull_id: e.target.value })}
            placeholder="Opcional"
            options={maleOptions}
          />
        )}
        {config.fields.includes('calf_id') && (
          <div className="space-y-3 rounded-2xl border border-manejai-sand/80 bg-manejai-cream/30 p-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCalfMode('existing')}
                className={cn(
                  'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition',
                  calfMode === 'existing'
                    ? 'border-manejai-forest bg-white text-manejai-forest shadow-sm'
                    : 'border-transparent text-manejai-bark/70 hover:bg-white/60',
                )}
              >
                Escolher animal
              </button>
              <button
                type="button"
                onClick={() => setCalfMode('new')}
                className={cn(
                  'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition',
                  calfMode === 'new'
                    ? 'border-manejai-forest bg-white text-manejai-forest shadow-sm'
                    : 'border-transparent text-manejai-bark/70 hover:bg-white/60',
                )}
              >
                Cadastrar cria
              </button>
            </div>
            {calfMode === 'existing' ? (
              <Select
                label="Bezerro(a)"
                required
                value={form.calf_id}
                onChange={(e) => {
                  const nextId = e.target.value;
                  const picked = animals?.animals.find((a) => String(a.id) === nextId);
                  setForm({
                    ...form,
                    calf_id: nextId,
                    calf_sex: picked?.sex ?? form.calf_sex,
                  });
                }}
                placeholder="Selecione…"
                options={allOptions}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-3">
                <Input
                  label="Código"
                  required
                  value={form.calf_internal_number}
                  onChange={(e) => setForm({ ...form, calf_internal_number: e.target.value })}
                  placeholder="Brinco / código"
                />
                <Input
                  label="Nome"
                  value={form.calf_name}
                  onChange={(e) => setForm({ ...form, calf_name: e.target.value })}
                  placeholder="Opcional"
                />
                <Select
                  label="Sexo"
                  value={form.calf_sex}
                  onChange={(e) => setForm({ ...form, calf_sex: e.target.value })}
                  options={[
                    { value: 'female', label: 'Fêmea' },
                    { value: 'male', label: 'Macho' },
                  ]}
                />
              </div>
            )}
          </div>
        )}
        {config.fields.includes('calf_sex') && calfMode === 'existing' && (
          <Select
            label="Sexo do bezerro"
            value={form.calf_sex}
            onChange={(e) => setForm({ ...form, calf_sex: e.target.value })}
            options={[
              { value: 'female', label: 'Fêmea' },
              { value: 'male', label: 'Macho' },
            ]}
          />
        )}
        {config.fields.includes('weight_kg') && (
          <WeightField
            label={type === 'weaning' ? 'Peso' : 'Peso'}
            title={type === 'weaning' ? 'Peso no desmame' : 'Peso'}
            required={type !== 'weaning'}
            value={form.weight_kg}
            onChange={(weight_kg) => setForm({ ...form, weight_kg })}
          />
        )}
        {config.fields.includes('milking_shift') && (
          <Select
            label="Turno"
            required
            value={form.milking_shift}
            onChange={(e) => setForm({ ...form, milking_shift: e.target.value })}
            options={MILKING_SHIFT_OPTIONS}
          />
        )}
        {config.fields.includes('production_liters') && (
          <Input
            label="Produção (litros)"
            required
            type="number"
            step="0.1"
            min="0"
            value={form.production_liters}
            onChange={(e) => setForm({ ...form, production_liters: e.target.value })}
          />
        )}
        {config.fields.includes('cover_event_id') && (
          <Input
            label="ID evento cobertura"
            type="number"
            value={form.cover_event_id}
            onChange={(e) => setForm({ ...form, cover_event_id: e.target.value })}
            hint="ID do evento de monta ou IA anterior"
          />
        )}
        {config.fields.includes('is_pregnant') && (
          <Select
            label="Resultado"
            value={form.is_pregnant}
            onChange={(e) => setForm({ ...form, is_pregnant: e.target.value })}
            options={[
              { value: 'true', label: 'Prenhe' },
              { value: 'false', label: 'Vazia' },
            ]}
          />
        )}
        {config.fields.includes('treatment_kind') && (
          <Select
            label="Tipo"
            required
            value={form.treatment_kind}
            onChange={(e) => setForm({ ...form, treatment_kind: e.target.value })}
            options={[
              { value: 'vaccination', label: 'Vacina' },
              { value: 'medication', label: 'Medicamento' },
            ]}
          />
        )}
        {config.fields.includes('product_name') && (
          <Input
            label="Produto"
            required
            value={form.product_name}
            onChange={(e) => setForm({ ...form, product_name: e.target.value })}
            placeholder="Ex.: Aftosa, Ivermectina…"
          />
        )}
        {config.fields.includes('dose') && (
          <Input
            label="Dose"
            value={form.dose}
            onChange={(e) => setForm({ ...form, dose: e.target.value })}
            placeholder="Ex.: 5 ml"
          />
        )}
        {config.fields.includes('route') && (
          <Select
            label="Via de aplicação"
            value={form.route}
            onChange={(e) => setForm({ ...form, route: e.target.value })}
            placeholder="Opcional"
            options={[
              { value: 'im', label: 'IM (intramuscular)' },
              { value: 'sc', label: 'SC (subcutânea)' },
              { value: 'iv', label: 'IV (intravenosa)' },
              { value: 'oral', label: 'Oral' },
              { value: 'topical', label: 'Tópica' },
              { value: 'other', label: 'Outra' },
            ]}
          />
        )}
        {config.fields.includes('product_batch') && (
          <Input
            label="Lote do frasco"
            value={form.product_batch}
            onChange={(e) => setForm({ ...form, product_batch: e.target.value })}
            placeholder="Opcional"
          />
        )}
        {config.fields.includes('next_dose_date') && (
          <DateInput
            label="Próxima dose"
            value={form.next_dose_date}
            onChange={(next_dose_date) => setForm({ ...form, next_dose_date })}
          />
        )}

        <Input
          label="Observações"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </form>
    </Modal>
  );
}
